import { PropsWithChildren } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ReactDatePickerProps } from "react-datepicker";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  StackProps,
  Text,
} from "@chakra-ui/react";
import DatePickerInput from "./DatePickerInput";

export type InputWrapperProps = {
  label?: string;
  id: string;
  isDisabled?: boolean;
  placeholder?: string;
  size?: string;
  uom?: string;
  spacing?: string;
  isInline?: boolean;
  value?: any;
  type?: string;
  w?: Record<string, string>;
  errorMsgOverride?: string;
};

const useInput = (id: string, isInline = false) => {
  const { register } = useFormContext();

  return {
    id,
    borderRadius: "4px",
    size: isInline ? "sm" : "md",
    backgroundColor: "white",
    ...register(id),
  };
};

export const InputWrapper = ({
  id,
  label,
  isDisabled = false,
  errorMsgOverride,
  children,
}: PropsWithChildren<InputWrapperProps>) => {
  const {
    formState: { errors },
  } = useFormContext();
  const errorMsg = errorMsgOverride ?? (errors?.[id]?.message as string);
  return (
    <FormControl isInvalid={!!errorMsg} isDisabled={isDisabled} id={id}>
      {label ? <FormLabel>{label}</FormLabel> : null}
      {children}
      <FormErrorMessage>{errorMsg}</FormErrorMessage>
    </FormControl>
  );
};

export const ReadOnlyFormStat = ({
  label,
  value,
  ...rest
}: {
  label?: string;
  value: string;
} & StackProps) => (
  <Stack spacing={2} {...rest}>
    <FormLabel>{label}</FormLabel>
    <Text
      textStyle="body2"
      fontWeight="medium"
      color="grey.700"
      textAlign="right"
    >
      {value}
    </Text>
  </Stack>
);

export const TextFormInput = ({
  id,
  isInline,
  value,
  ...rest
}: InputWrapperProps) => {
  const inputProps = useInput(id, isInline);
  return (
    <InputWrapper id={id} {...rest}>
      <InputGroup>
        <Input
          w={rest.w}
          type={rest.type}
          placeholder={value}
          {...inputProps}
        />
        {rest.uom && (
          <InputRightElement>
            <Text>{rest.uom}</Text>
          </InputRightElement>
        )}
      </InputGroup>
    </InputWrapper>
  );
};

export const DatePickerFormInput = ({
  id,
  minDate,
  maxDate,
  ...rest
}: InputWrapperProps & Pick<ReactDatePickerProps, "minDate" | "maxDate">) => {
  const { control } = useFormContext();

  return (
    <InputWrapper id={id} {...rest}>
      <Controller
        name={id}
        control={control}
        render={({ field: { onChange, ref, value, onBlur, name } }) => (
          <DatePickerInput
            onChange={onChange}
            ref={ref}
            onBlur={onBlur}
            value={value}
            name={name}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      />
    </InputWrapper>
  );
};
